import { useCallback, useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import { useDigitalLicensesWithLoading } from 'lib/api/hooks/digitalLicenses'
import { navigationEvent as createNavigationEvent } from 'layers/navigation/store/operations'
import { operations as lmsOperations } from 'store/lmsManager'
import {
  operations as curriculumCourseOperations,
  selectors as curriculumCourseSelectors,
} from 'store/curriculumCourseManager'
import { transformCurriculumCourseTrees } from 'layers/content/v3/transforms/transformCurriculumCourseTree'
import {
  parseGradeAndProgram,
  getCourseLinks,
  coursesWithNewContentfulIds,
} from 'utils/lmsHelpers'
import { getClassCourseTreeLookupKeys } from 'components/ProductLessons/classListHelpers'

import { getProgramPreferencesFromArray } from 'utils/sitePreferenceHelpers'
import {
  DIGITAL_WELL_BEING_PRODUCT_NAME,
  LESSONS_PROGRAM_CONFIG,
} from './constants'
import {
  processCourseTreeCollection,
  selectLessonsCurriculumClasses,
} from './data'

const findCurrentSiteV4 = ({ sites, activeSiteId }) => {
  if (!Array.isArray(sites) || activeSiteId == null) {
    return null
  }

  return (
    sites.find(
      site =>
        site?.id === activeSiteId || String(site?.id) === String(activeSiteId),
    ) || null
  )
}

export const useLessonsState = activeSiteId =>
  useSelector(state => {
    const { lmsManager, userContextManager, productManager } = state || {}

    return {
      currentSiteV4: findCurrentSiteV4({
        sites: state?.ssapiManager?.sites,
        activeSiteId,
      }),
      instructorData: lmsManager?.instructorData,
      lms: lmsManager,
      pathName: state?.router?.location?.pathname,
      productTabTitle: productManager?.productMap?.name || '',
      userAccess: state?.userAccessManager?.access,
      userContext: userContextManager,
      userProfile: state?.userProfileManager,
    }
  }, shallowEqual)

export const useLessonsActions = () => {
  const dispatch = useDispatch()

  const navigationEvent = useCallback(
    (...args) => dispatch(createNavigationEvent(...args)),
    [dispatch],
  )
  const createCourseInstanceV2ProgramFlex = useCallback(
    (...args) =>
      dispatch(lmsOperations.createCourseInstanceV2ProgramFlex(...args)),
    [dispatch],
  )
  const createCourseInstanceByCourse = useCallback(
    params => dispatch(lmsOperations.createCourseInstanceByCourse(params)),
    [dispatch],
  )
  const deletingCourseCreatedStatus = useCallback(
    (...args) => dispatch(lmsOperations.deletingCourseCreatedStatus(...args)),
    [dispatch],
  )
  const deleteCourseInstance = useCallback(
    (...args) => dispatch(lmsOperations.deleteCourseInstanceV2(...args)),
    [dispatch],
  )
  const fetchInstructorData = useCallback(
    (...args) => dispatch(lmsOperations.fetchInstructorProgramFlexGet(...args)),
    [dispatch],
  )
  const updateCourseInstance = useCallback(
    (...args) => dispatch(lmsOperations.updateCourseInstanceV2(...args)),
    [dispatch],
  )

  return {
    createCourseInstanceByCourse,
    createCourseInstanceV2ProgramFlex,
    deleteCourseInstance,
    deletingCourseCreatedStatus,
    fetchInstructorData,
    navigationEvent,
    updateCourseInstance,
  }
}

export const useLessonsPageData = ({
  courseManager,
  currentSite,
  instructorData,
  match,
  userContext,
}) => {
  // CreateClass needs license data before the modal opens.
  useDigitalLicensesWithLoading()

  const { program, programKey } = useMemo(
    () => parseGradeAndProgram(match?.url),
    [match?.url],
  )

  const classes = useMemo(
    () =>
      selectLessonsCurriculumClasses({
        instructorData,
        userContext,
        programKey,
      }),
    [instructorData, userContext, programKey],
  )

  const courseLinks = useMemo(
    () => getCourseLinks(coursesWithNewContentfulIds({ programType: program })),
    [program],
  )

  const classCourseManager = useClassCourseTrees({
    classes,
    programKey,
    siteId: currentSite?.siteId,
  })

  const resolvedCourseManager = classCourseManager ?? courseManager

  const courseTreesWithDisplayTitles = useMemo(
    () => processCourseTreeCollection(resolvedCourseManager?.courseTrees),
    [resolvedCourseManager?.courseTrees],
  )

  const lessonCompletionGoalValue = useMemo(() => {
    const accessCode = LESSONS_PROGRAM_CONFIG[programKey]?.preferencesAccessCode

    if (!accessCode) {
      return null
    }

    const programPreferences = getProgramPreferencesFromArray(
      currentSite?.preferences,
      { accessCode },
    )

    return programPreferences?.lessonCompletionGoalValue ?? null
  }, [currentSite, programKey])

  return {
    classes,
    courseLinks,
    courseManager: resolvedCourseManager,
    courseTreesWithDisplayTitles,
    lessonCompletionGoalValue,
    programKey,
  }
}

export const useAutoOpenTrigger = () => {
  const [autoOpen, setAutoOpen] = useState(false)
  const open = useCallback(() => setAutoOpen(true), [])

  useEffect(() => {
    if (autoOpen) {
      setAutoOpen(false)
    }
  }, [autoOpen])

  return {
    autoOpen,
    open,
  }
}

const getDwbCourseCodesForClasses = ({ classes, programKey }) => {
  if (programKey !== DIGITAL_WELL_BEING_PRODUCT_NAME) return []

  const courseCodes = new Set()
  classes?.forEach(classItem => {
    getClassCourseTreeLookupKeys({
      classItem,
      program: DIGITAL_WELL_BEING_PRODUCT_NAME,
    }).forEach(candidate => {
      if (candidate?.includes('_dwb_tier-1')) {
        courseCodes.add(candidate)
      }
    })
  })

  return Array.from(courseCodes)
}

export const useClassCourseTrees = ({ classes, programKey, siteId }) => {
  const dispatch = useDispatch()
  const contentGroupCode = 'dwb'

  const courseCodes = useMemo(
    () => getDwbCourseCodesForClasses({ classes, programKey }),
    [classes, programKey],
  )

  const selection = useSelector(
    curriculumCourseSelectors.selectCurriculumCourseTrees(
      contentGroupCode,
      courseCodes,
      siteId,
    ),
  )

  useEffect(() => {
    if (programKey !== DIGITAL_WELL_BEING_PRODUCT_NAME) return
    if (siteId == null || courseCodes.length === 0) return

    courseCodes.forEach(courseCode => {
      if (selection.courseTrees?.[courseCode]) return
      dispatch(
        curriculumCourseOperations.fetchCurriculumCourseTree({
          contentGroupCode,
          courseCode,
          siteId,
        }),
      )
    })
    // We intentionally exclude `selection` from deps to avoid refetching
    // immediately after the slice updates; per-course existence is checked
    // each pass against the current cached map.
  }, [courseCodes, dispatch, programKey, siteId])

  const courseTrees = useMemo(
    () => transformCurriculumCourseTrees(selection.courseTrees),
    [selection.courseTrees],
  )

  if (programKey !== DIGITAL_WELL_BEING_PRODUCT_NAME) {
    return null
  }

  return {
    courseTrees,
    error: selection.error,
    loading: selection.loading,
  }
}
